import React, { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bluetooth, ChevronRight, ChevronLeft, CheckCircle2, Settings, Volume2, Play, X, Pencil } from "lucide-react";
import { useLang } from "@/i18n";

const STORAGE_KEY = "hm_bt_speaker_name";

function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macos";
  if (/Windows/i.test(ua)) return "windows";
  return "desktop";
}

const STEPS_BY_PLATFORM = {
  ios: [
    { title: "Zet je speaker aan", body: "Zet je Bluetooth speaker aan en activeer koppel-modus (meestal een knop 3 seconden indrukken tot het lampje knippert)." },
    { title: "Open Instellingen", body: "Ga op je iPhone/iPad naar Instellingen → Bluetooth en zet Bluetooth aan." },
    { title: "Selecteer je speaker", body: "Tik op de naam van je speaker in de lijst 'Andere apparaten' om te koppelen." },
    { title: "Speel Hitmaster!", body: "Zodra 'Verbonden' verschijnt speelt al het geluid via je speaker. Voer hieronder de naam in om het te bevestigen." },
  ],
  android: [
    { title: "Zet je speaker aan", body: "Zet je Bluetooth speaker aan en activeer koppel-modus (meestal een knop 3 seconden indrukken tot het lampje knippert)." },
    { title: "Open Instellingen", body: "Veeg omlaag → houd het Bluetooth icoon ingedrukt. Of ga naar Instellingen → Verbonden apparaten → Bluetooth." },
    { title: "Koppel apparaat", body: "Tik op 'Koppel nieuw apparaat' en selecteer je speaker uit de lijst." },
    { title: "Speel Hitmaster!", body: "Zodra 'Verbonden' verschijnt speelt al het geluid via je speaker. Voer hieronder de naam in om het te bevestigen." },
  ],
  macos: [
    { title: "Zet je speaker aan", body: "Zet je Bluetooth speaker aan en activeer koppel-modus." },
    { title: "Open Systeeminstellingen", body: "Klik op het  menu → Systeeminstellingen → Bluetooth en zet Bluetooth aan." },
    { title: "Verbind", body: "Klik op 'Verbind' naast de naam van je speaker in de lijst." },
    { title: "Speel Hitmaster!", body: "Selecteer je speaker als audio-uitvoer via het volume-icoon in de menubalk. Voer hieronder de naam in." },
  ],
  windows: [
    { title: "Zet je speaker aan", body: "Zet je Bluetooth speaker aan en activeer koppel-modus." },
    { title: "Open Instellingen", body: "Start → Instellingen → Bluetooth en apparaten. Zet Bluetooth aan." },
    { title: "Voeg apparaat toe", body: "Klik 'Apparaat toevoegen' → 'Bluetooth' → selecteer je speaker uit de lijst." },
    { title: "Speel Hitmaster!", body: "Rechtsklik op het luidsprekericoon in de taakbalk → Afspeelapparaten → selecteer je Bluetooth speaker. Voer hieronder de naam in." },
  ],
  desktop: [
    { title: "Zet je speaker aan", body: "Zet je Bluetooth speaker aan en activeer koppel-modus." },
    { title: "Open Bluetooth instellingen", body: "Open de Bluetooth instellingen van je besturingssysteem en zet Bluetooth aan." },
    { title: "Koppel je speaker", body: "Zoek naar apparaten en selecteer je speaker om te koppelen." },
    { title: "Speel Hitmaster!", body: "Zorg dat je speaker als audio-uitgang geselecteerd is. Voer hieronder de naam in." },
  ],
};

const STEP_ICONS = [Bluetooth, Settings, Volume2, Play];

export const BluetoothGuide = () => {
  const { t: tr } = useLang();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [speakerName, setSpeakerName] = useState("");
  const [connectedName, setConnectedName] = useState("");

  const platform = useMemo(() => detectPlatform(), []);
  const steps = STEPS_BY_PLATFORM[platform] || STEPS_BY_PLATFORM.desktop;
  const isLast = step === steps.length - 1;
  const StepIcon = STEP_ICONS[step] || Bluetooth;
  const platformLabel = { ios: "iPhone / iPad", android: "Android", macos: "Mac", windows: "Windows", desktop: "Desktop" }[platform] || "";

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY) || "";
      setConnectedName(v);
      setSpeakerName(v);
    } catch {}
  }, []);

  const openWizard = () => { setStep(0); setSpeakerName(connectedName || ""); setOpen(true); };

  const openSystemBluetooth = async () => {
    if (typeof navigator !== "undefined" && navigator.bluetooth?.requestDevice) {
      try {
        const dev = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: [] });
        if (dev?.name) setSpeakerName(dev.name);
      } catch { /* cancelled or unsupported */ }
    }
  };

  const confirmConnected = () => {
    const name = (speakerName || "").trim();
    if (!name) return;
    try { localStorage.setItem(STORAGE_KEY, name); } catch {}
    setConnectedName(name);
    setOpen(false);
  };

  const disconnect = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setConnectedName("");
    setSpeakerName("");
  };

  return (
    <>
      {connectedName ? (
        <div
          data-testid="bluetooth-connected-badge"
          className="flex w-full items-center gap-3 rounded-2xl transition-all"
          style={{
            padding: "clamp(0.55rem, 2vw, 0.85rem) clamp(0.85rem, 3vw, 1.25rem)",
            backgroundColor: "rgba(5, 150, 105, 0.18)",
            border: "4px solid #FFF9EE",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            fontSize: "clamp(0.9rem, 2.8vw, 1.1rem)",
            boxShadow:
              "0 18px 40px rgba(0,0,0,0.30), 0 8px 16px rgba(0,0,0,0.22), inset 0 3px 8px rgba(255,255,255,0.45), inset 0 -6px 16px rgba(0,0,0,0.32)",
          }}
        >
          <span
            className="flex items-center justify-center rounded-full shrink-0"
            style={{ width: "2.1em", height: "2.1em", backgroundColor: "#059669", border: "2px solid white" }}
          >
            <Bluetooth className="w-[1.1em] h-[1.1em] text-white" strokeWidth={2.6} />
          </span>
          <div className="text-white font-poppins" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.55)" }}>
            <div className="font-bold leading-tight">{tr("bt.connectedWith")}</div>
            <div data-testid="bluetooth-speaker-name" className="font-bold leading-tight" style={{ fontWeight: 800 }}>
              {connectedName}
            </div>
          </div>
          <div className="flex items-center gap-1 ml-auto shrink-0">
            <button
              type="button"
              onClick={openWizard}
              data-testid="bluetooth-edit-btn"
              aria-label="Wijzig speaker"
              className="rounded-full bg-white/25 hover:bg-white/40 text-white border-2 border-white/60 transition-colors"
              style={{ padding: "0.35rem" }}
            >
              <Pencil className="w-4 h-4" strokeWidth={2.4} />
            </button>
            <button
              type="button"
              onClick={disconnect}
              data-testid="bluetooth-disconnect-btn"
              aria-label="Verbreek verbinding"
              className="rounded-full bg-white/25 hover:bg-red-500/70 text-white border-2 border-white/60 transition-colors"
              style={{ padding: "0.35rem" }}
            >
              <X className="w-4 h-4" strokeWidth={2.6} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="bluetooth-guide-btn"
          onClick={openWizard}
          className="flex w-full items-center justify-center gap-3 bg-white/15 hover:bg-white/25 backdrop-blur-xl text-white rounded-2xl transition-all group"
          style={{
            padding: "clamp(0.65rem, 2.5vw, 1rem) clamp(1.25rem, 4vw, 1.75rem)",
            fontSize: "clamp(0.95rem, 3vw, 1.15rem)",
            border: "4px solid #FFF9EE",
            boxShadow:
              "0 18px 40px rgba(0,0,0,0.30), 0 8px 16px rgba(0,0,0,0.22), inset 0 3px 8px rgba(255,255,255,0.45), inset 0 -6px 16px rgba(0,0,0,0.32)",
          }}
        >
          <span
            className="flex items-center justify-center rounded-full bg-[#2563eb] border-2 border-white shrink-0 group-hover:rotate-6 transition-transform"
            style={{ width: "2.25em", height: "2.25em" }}
          >
            <Bluetooth className="w-[1.1em] h-[1.1em] text-white" strokeWidth={2.6} />
          </span>
          <span className="font-poppins font-bold tracking-wide">{tr("bt.connect")}</span>
        </button>
      )}

      <Dialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setStep(0); }}
      >
        <DialogContent
          data-testid="bluetooth-guide-modal"
          className="max-w-lg border-4 border-navy bg-cream text-navy p-0 overflow-hidden"
        >
          <div className="p-6 sm:p-7">
            <DialogHeader className="mb-2">
              <DialogTitle className="font-bebas tracking-wider text-4xl sm:text-5xl text-navy text-left flex items-center gap-3">
                <span
                  className="inline-flex items-center justify-center rounded-full text-white shrink-0"
                  style={{ width: 44, height: 44, backgroundColor: "#2563eb" }}
                >
                  <Bluetooth className="w-6 h-6" strokeWidth={2.5} />
                </span>
                Bluetooth
              </DialogTitle>
            </DialogHeader>
            <div className="font-poppins text-navy/70 text-sm mb-4" style={{ fontWeight: 500 }}>
              {tr("bt.instructionsFor")} <b>{platformLabel}</b>
            </div>

            {/* Progress dots */}
            <div className="flex items-center gap-2 mb-5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setStep(i)}
                  aria-label={`Stap ${i + 1}`}
                  className="flex-1 h-2 rounded-full transition-colors"
                  style={{ backgroundColor: i <= step ? "#2563eb" : "rgba(30,27,75,0.15)" }}
                />
              ))}
            </div>

            {/* Current step content */}
            <div className="min-h-[210px] flex gap-4">
              <div
                className="shrink-0 flex items-center justify-center rounded-2xl border-2 border-navy"
                style={{ width: 72, height: 72, backgroundColor: "rgba(37,99,235,0.10)" }}
              >
                <StepIcon className="w-9 h-9" style={{ color: "#2563eb" }} strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <div className="font-poppins text-navy" style={{ fontWeight: 700, fontSize: "16pt", lineHeight: 1.2 }}>
                  {tr("bt.step")} {step + 1}. {steps[step].title}
                </div>
                <p className="mt-2 font-poppins text-navy" style={{ fontWeight: 300, fontSize: "12pt", lineHeight: 1.55 }}>
                  {steps[step].body}
                </p>
                {step === 1 && typeof navigator !== "undefined" && navigator.bluetooth?.requestDevice && (
                  <button
                    type="button"
                    onClick={openSystemBluetooth}
                    className="mt-3 inline-flex items-center gap-2 text-white font-poppins font-bold rounded-full border-2 border-white/80"
                    style={{ backgroundColor: "#2563eb", padding: "0.5rem 1rem", fontSize: "11pt" }}
                  >
                    <Bluetooth className="w-4 h-4" /> {tr("bt.openPicker")}
                  </button>
                )}
                {isLast && (
                  <div className="mt-3 space-y-2">
                    <label className="block font-poppins text-navy" style={{ fontWeight: 600, fontSize: "11pt" }}>
                      {tr("bt.speakerName")}
                    </label>
                    <input
                      data-testid="bluetooth-speaker-name-input"
                      value={speakerName}
                      onChange={(e) => setSpeakerName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") confirmConnected(); }}
                      placeholder="bv. JBL Charge 5, Sonos, AirPods…"
                      className="w-full bg-white border-2 border-navy rounded-xl px-3 py-2 font-poppins text-navy focus:outline-none focus:ring-4 focus:ring-blue-400/40"
                      style={{ fontWeight: 500, fontSize: "12pt" }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Nav buttons */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="bluetooth-guide-prev"
                className="inline-flex items-center gap-1 font-bebas uppercase tracking-widest text-navy px-4 py-2 rounded-full border-2 border-navy bg-white/60 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" /> {tr("bt.prev")}
              </button>
              {!isLast ? (
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                  data-testid="bluetooth-guide-next"
                  className="inline-flex items-center gap-1 font-bebas uppercase tracking-widest text-white px-5 py-2 rounded-full border-2 border-navy hover:brightness-110"
                  style={{ backgroundColor: "#2563eb", boxShadow: "0 8px 20px rgba(37,99,235,0.35)" }}
                >
                  Volgende <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={confirmConnected}
                  disabled={!speakerName.trim()}
                  data-testid="bluetooth-guide-done"
                  className="inline-flex items-center gap-1 font-bebas uppercase tracking-widest text-white px-5 py-2 rounded-full border-2 border-navy disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "#059669", boxShadow: "0 8px 20px rgba(5,150,105,0.35)" }}
                >
                  <CheckCircle2 className="w-5 h-5" /> {tr("bt.connected")}
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BluetoothGuide;
